import React, { useCallback, useEffect, useMemo, useState } from "react";
import NewFilterContainer from "../../../../components/NewFilterContainer ";
import Tooltip from "../../../../components/tooltip";
import MainPageContainer from "../../../../components/MainPageContainer";
import SecondaryHeader from "../../../Rajasthan/Demand/SecondaryHeader";
import { ChartAreaLoader } from "../../../../components/Loading";
import { formatDateForAPI } from "../../../Rajasthan/Utility/const";
import { useSelector, useDispatch } from "react-redux";
import { setLoadingFilterName } from "../../../../store/state/FilterLoadingState";
import {
  fetchExchangeSlotData,
  getExchangeAnalysisData,
  getCollectiveMetrics,
} from "../../../../Rest_api/RestApi";
import { ExchangeMetrics } from "../../../../models/metrics";
import { ReMixChart } from "../../../../components/recharts/ReMixCharts";
import {
  renderHourTick,
  renderQuarterTick,
} from "../../../../components/recharts/components";
import { ChartTable } from "../../../../components/table/SummaryTable";
import LegendSummarySlotTable from "../../../../components/table/SummaryTableSlot";
import {
  BrushStart,
  ChartType,
  LegendKey,
} from "../../../../models/chart_model";
import { AddDatesWithFileName } from "../../../../helpers/helper";
import FilterMenu from "../../../../components/FilterMenu";
import Modal from "../../../../components/Model";
import BuyerSellerChart from "./TopBuyersSellersPopup";
import { ExchangeDataTabs } from "../../../Rajasthan/Demand/Data";
import BlockRangeSelector from "../../../../components/BlockRangeSelector/BlockRangeSelector";
import { ReactComponent as Info } from "../../../../icons/Exchange-Analysis.svg";
import { useLegendFilter } from "../../../../hooks/useLegendFilter";
import MetricChip from "../../../../components/Metrics";
import { ReactComponent as PeakIcon } from "../../../../icons/peakicon.svg";
import { ReactComponent as AvgIcon } from "../../../../icons/AvgIcon.svg";
import { ReactComponent as TotalIcon } from "../../../../icons/TotalIcon.svg";
import { normalizeDisplayLabel } from "../../../../utility/displayText";
import { GripVertical, Crown, Zap } from "lucide-react";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";

const DataTabs = ["comparative", "cumulative"];

interface ExchangeAnalysisDataItem {
  x_axis: string;
  second_x_axis: string | null;
  price: number;
  prev_year_price: number | null;
  rolling_actual_30_day_avgs: number | null;
  prev_year_rolling_actual_30_day_avgs: number | null;
  gna_forecasted_price: number | null;
  exchange__name: string;
  product__name: string;
  mcv_mw: number;
  mcv_mwh: number;
  prev_year_mcv_mw: number | null;
  prev_year_mcv_mwh: number | null;
  rolling_actual_30_day_avgs_mcv_mw: number | null;
  prev_year_rolling_actual_30_day_avgs_mcv_mw: number | null;
  purchase_bids_mw: number;
  purchase_bids_mwh: number;
  prev_year_purchase_bids_mw: number | null;
  prev_year_purchase_bids_mwh: number | null;
  rolling_actual_30_day_avgs_purchase_bids_mw: number | null;
  prev_year_rolling_actual_30_day_avgs_purchase_bids_mw: number | null;
  sell_bids_mw: number;
  sell_bids_mwh: number;
  prev_year_sell_bids_mw: number | null;
  prev_year_sell_bids_mwh: number | null;
  rolling_actual_30_day_avgs_sell_bids_mw: number | null;
  prev_year_rolling_actual_30_day_avgs_sell_bids_mw: number | null;
}

export interface ExchangeAnalysisParams {
  interval: "slot" | "day" | "week" | "month";
  price_type: "simple_avg" | "wt_avg";
  exchange_name: string; // "IEX" | "PXIL" | "HPX" | "all"
  product_name: string; // "DAM" | "RTM" | "GDAM" | "HPDAM" | "all"
  start_date?: string;
  end_date?: string;
  function?: string;
  blocks?: (string | number)[];
}

export interface ExchangeAnalysisData {
  x_axis: string;
  second_x_axis: string | null;
  price: number;
  prev_year_price: number | null;
  rolling_actual_30_day_avgs: number | null;
  prev_year_rolling_actual_30_day_avgs: number | null;
  gna_forecasted_price: number | null;
  exchange__name: string;
  product__name: string;
  mcv_mw: number;
  mcv_mwh: number;
  prev_year_mcv_mw: number | null;
  prev_year_mcv_mwh: number | null;
  rolling_actual_30_day_avgs_mcv_mw: number | null;
  prev_year_rolling_actual_30_day_avgs_mcv_mw: number | null;
  purchase_bids_mw: number;
  purchase_bids_mwh: number;
  prev_year_purchase_bids_mw: number | null;
  prev_year_purchase_bids_mwh: number | null;
  rolling_actual_30_day_avgs_purchase_bids_mw: number | null;
  prev_year_rolling_actual_30_day_avgs_purchase_bids_mw: number | null;
  sell_bids_mw: number;
  sell_bids_mwh: number;
  prev_year_sell_bids_mw: number | null;
  prev_year_sell_bids_mwh: number | null;
  rolling_actual_30_day_avgs_sell_bids_mw: number | null;
  prev_year_rolling_actual_30_day_avgs_sell_bids_mw: number | null;
}

const variableFieldMapping: Record<string, Record<string, string>> = {
  price: {
    prev_year: "prev_year_price",
    rolling_avg: "rolling_actual_30_day_avgs",
    prev_year_rolling_avg: "prev_year_rolling_actual_30_day_avgs",
  },
  mcv: {
    prev_year: "prev_year_mcv",
    rolling_avg: "rolling_actual_30_day_avgs_mcv",
    prev_year_rolling_avg: "prev_year_rolling_actual_30_day_avgs_mcv",
  },
  purchase_bids: {
    prev_year: "prev_year_purchase_bids",
    rolling_avg: "rolling_actual_30_day_avgs_purchase_bids",
    prev_year_rolling_avg: "prev_year_rolling_actual_30_day_avgs_purchase_bids",
  },
  sell_bids: {
    prev_year: "prev_year_sell_bids",
    rolling_avg: "rolling_actual_30_day_avgs_sell_bids",
    prev_year_rolling_avg: "prev_year_rolling_actual_30_day_avgs_sell_bids",
  },
};

const chartSuffixes = [
  {
    suffix: "price",
    displayName: "MCP",
    stackSuffix: "price",
    chartType: ChartType.Line,
    unit: "Rs/KWh",
    yAxisId: "right",
  },
  {
    suffix: "mcv",
    displayName: "MCV",
    stackSuffix: "mcv",
    chartType: ChartType.Bar,
    unit: "MW/MWh",
    yAxisId: "left",
  },
  {
    suffix: "purchase_bids",
    displayName: "Purchase Bid",
    stackSuffix: "purchase_bids",
    chartType: ChartType.Line,
    unit: "MW/MWh",
    yAxisId: "left",
  },
  {
    suffix: "sell_bids",
    displayName: "Sell Bid",
    stackSuffix: "sell_bids",
    chartType: ChartType.Line,
    unit: "MW/MWh",
    yAxisId: "left",
  },
];

const typeFilterMetrics = chartSuffixes;

const variableFilterOptions = [
  { key: "actual", label: "Actual" },
  { key: "prev_year", label: "Prev Year Actual" },
  { key: "rolling_avg", label: "30D Roll Avg" },
  { key: "prev_year_rolling_avg", label: "Prev Year 30D Roll Avg" },
];

let Intervals = [
  {
    label: "Time Slot",
    key: "slot" as const,
    values: ["x_axis", "second_x_axis"],
    isTime: true,
  },
  {
    label: "Date",
    key: "day" as const,
    values: ["x_axis"],
    isTime: false,
  },
  {
    label: "Week",
    key: "week" as const,
    values: ["x_axis", "second_x_axis"],
    isTime: false,
  },
  {
    label: "Month",
    key: "month" as const,
    values: ["x_axis"],
    isTime: false,
  },
];

const SLOT_INTERVAL =
  Intervals.find((interval) => interval.key === "slot") || Intervals[0];

const PRICE_TYPE_OPTIONS = [
  { key: "simple_avg" as const, label: "Simple Avg" },
  { key: "wt_avg" as const, label: "Weighted Avg" },
];

const UNIT_OPTIONS = ["MW", "MWh"];

const exchanges = ["HPX", "IEX", "PXIL"];
const products = ["DAM", "GDAM", "HPDAM", "RTM"];

const ExchangeAnalysis = () => {
  const activeTab = useSelector((state: any) => state.switchTabs.activeTab);
  const DefaultDates = useSelector(
    (state: any) => state.defaultDatesSetter.defaultDates,
  );
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const [activefunctionTab, setActivefunctionTab] = useState(DataTabs[0]);
  const [rawData, setRawData] = useState<ExchangeAnalysisDataItem[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [summaryIntervalData, setSummaryIntervalData] = useState<any[]>([]);
  const [TableData, setTableData] = useState<any[]>([]);
  const [selectedInterval, setSelectedInterval] = useState(Intervals[0]);
  const [selectedPriceType, setSelectedPriceType] = useState(
    PRICE_TYPE_OPTIONS[1],
  );
  const [selectedUnit, setSelectedUnit] = useState(UNIT_OPTIONS[0]);
  const [selectedBlock, setSelectedBlock] = useState<any[]>([]);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [uniqueBlock, setUniqueBlock] = useState<any[]>([]);
  const [selectedTypeMetrics, setSelectedTypeMetrics] = useState<any[]>([
    typeFilterMetrics[0],
    typeFilterMetrics[1],
  ]);
  const [selectedVariableOptions, setSelectedVariableOptions] = useState<
    string[]
  >(["actual"]);
  const [loading, setLoading] = useState(false);
  const [selectedExchanges, setSelectedExchanges] = useState<string[]>(["IEX"]);
  const [selectedProducts, setSelectedProducts] = useState<string[]>(["DAM"]);
  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState<any>({});
  const [modalHeader, setModalHeader] = useState<any>({});
  const [metrics, setMetrics] = useState<ExchangeMetrics | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [selectedIntervalRawData, setSelectedIntervalRawData] = useState<
    ExchangeAnalysisDataItem[] | null
  >(null);
  const dispatch = useDispatch();

  const isChartView = dataTab?.label === "Chart";
  const isTableView = dataTab?.label === "Table";
  const effectiveInterval = isChartView || isTableView ? selectedInterval : SLOT_INTERVAL;

  const fetchData = useCallback(async () => {
    if (!DefaultDates[activeTab]?.defaultRangeDate) {
      return;
    }

    setLoading(true);
    try {
      const dates = DefaultDates[activeTab].defaultRangeDate;
      const startDateStr = formatDateForAPI(dates.startDate);
      const endDateStr = formatDateForAPI(dates.endDate);

      const exchangeName =
        selectedExchanges.length === exchanges.length
          ? "all"
          : selectedExchanges.join(",");

      const productName =
        selectedProducts.length === products.length
          ? "all"
          : selectedProducts.join(",");

      const data = await getExchangeAnalysisData({
        interval: effectiveInterval.key,
        price_type: selectedPriceType.key,
        exchange_name: exchangeName,
        product_name: productName,
        start_date: startDateStr,
        end_date: endDateStr,
        function: activefunctionTab,
        blocks: selectedBlock.map((block) => block.value),
      } as ExchangeAnalysisParams);

      setRawData(data);

      if (effectiveInterval.key !== selectedInterval.key) {
        try {
          const intervalData = await getExchangeAnalysisData({
            interval: selectedInterval.key,
            price_type: selectedPriceType.key,
            exchange_name: exchangeName,
            product_name: productName,
            start_date: startDateStr,
            end_date: endDateStr,
            function: activefunctionTab,
            blocks: selectedBlock.map((block) => block.value),
          } as ExchangeAnalysisParams);
          setSelectedIntervalRawData(intervalData);
        } catch (intervalError) {
          console.error(
            "Error fetching selected interval data:",
            intervalError,
          );
          setSelectedIntervalRawData(null);
        }
      } else {
        setSelectedIntervalRawData(data);
      }

      if (effectiveInterval.key === "slot") {
        setUniqueBlock(
          Array.from(
            new Set(data.map((item: ExchangeAnalysisDataItem) => item.x_axis)),
          ),
        );
      } else {
        setUniqueBlock([]);
      }
    } catch (error) {
      console.error("Error fetching exchange analysis data:", error);
      setRawData([]);
    } finally {
      setLoading(false);
      dispatch(setLoadingFilterName(""));
    }
  }, [
    DefaultDates,
    activeTab,
    effectiveInterval.key,
    selectedInterval.key,
    selectedPriceType.key,
    activefunctionTab,
    dataTab,
    selectedExchanges,
    selectedProducts,
    selectedBlock,
  ]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchMetrics = useCallback(async () => {
    if (!DefaultDates[activeTab]?.defaultRangeDate) {
      return;
    }

    setMetricsLoading(true);
    try {
      const dates = DefaultDates[activeTab].defaultRangeDate;
      const startDateStr = formatDateForAPI(dates.startDate);
      const endDateStr = formatDateForAPI(dates.endDate);

      const exchangeName =
        selectedExchanges.length === exchanges.length
          ? undefined
          : selectedExchanges.length > 0
            ? selectedExchanges
            : undefined;

      const productName =
        selectedProducts.length === products.length
          ? undefined
          : selectedProducts.length > 0
            ? selectedProducts
            : undefined;

      const metricsData = await getCollectiveMetrics({
        ranges: [
          {
            startDate: startDateStr,
            endDate: endDateStr,
            exchangeName: exchangeName,
            productName: productName,
            blocks: selectedBlock.map((block) => block.value),
          },
        ],
        aggregated: true,
      });

      // When aggregated=true, API returns a single ExchangeMetrics object
      setMetrics(Array.isArray(metricsData) ? metricsData[0] : metricsData);
    } catch (error) {
      console.error("Error fetching metrics:", error);
      setMetrics(null);
    } finally {
      setMetricsLoading(false);
    }
  }, [
    DefaultDates,
    activeTab,
    selectedBlock,
    selectedExchanges,
    selectedProducts,
  ]);

  useEffect(() => {
    fetchMetrics();
  }, [fetchMetrics]);

  const processDataForInterval = useCallback(
    (
      sourceData: ExchangeAnalysisDataItem[],
      intervalConfig: (typeof Intervals)[number],
    ) => {
      let filteredData = [...sourceData];

      if (activefunctionTab === "comparative") {
        if (
          selectedExchanges.length > 0 &&
          selectedExchanges.length < exchanges.length
        ) {
          filteredData = filteredData.filter((item) =>
            selectedExchanges.includes(item.exchange__name),
          );
        }

        if (
          selectedProducts.length > 0 &&
          selectedProducts.length < products.length
        ) {
          filteredData = filteredData.filter((item) =>
            selectedProducts.includes(item.product__name),
          );
        }
      }

      if (selectedBlock.length > 0 && intervalConfig.key === "slot") {
        filteredData = filteredData.filter((item) =>
          selectedBlock.map((block) => block.value).includes(item.x_axis),
        );
      }

      const groupedData: any = {};

      filteredData.forEach((item) => {
        let secondAxis = item.second_x_axis;
        if (secondAxis && typeof secondAxis === "string") {
          const dm = /^(\d{2})-(\d{2})-(\d{4})$/.exec(secondAxis);
          if (dm) {
            secondAxis = `${dm[3]}-${dm[2]}-${dm[1]}`;
          }
        }

        let xAxis = item.x_axis;
        if (xAxis && typeof xAxis === "string") {
          const dm = /^(\d{2})-(\d{2})-(\d{4})$/.exec(xAxis);
          if (dm) {
            xAxis = `${dm[3]}-${dm[2]}-${dm[1]}`;
          }
        }

        let key: string;
        if (
          (intervalConfig.isTime ||
            intervalConfig.values.includes("second_x_axis")) &&
          secondAxis
        ) {
          key = `${secondAxis}_${xAxis}`;
        } else {
          key = xAxis;
        }

        if (!groupedData[key]) {
          groupedData[key] = {
            x_axis: xAxis,
            second_x_axis: secondAxis,
          };
        }

        const exchangeName = item.exchange__name || "";
        const productName = item.product__name || "";
        const prefix =
          activefunctionTab === "cumulative"
            ? "Cumulative"
            : exchangeName && productName
              ? `${exchangeName}_${productName}`
              : "Cumulative";

        selectedTypeMetrics.forEach((typeMetric) => {
          let actualSuffix = typeMetric.suffix;
          if (typeMetric.unit === "MW/MWh") {
            actualSuffix = `${typeMetric.suffix}_${selectedUnit.toLowerCase()}`;
          }

          const dataKey = `${prefix}_${actualSuffix}`;

          if (selectedVariableOptions.includes("actual")) {
            groupedData[key][dataKey] =
              item[actualSuffix as keyof ExchangeAnalysisDataItem];
          }

          selectedVariableOptions.forEach((variableKey) => {
            if (variableKey === "actual") {
              return;
            }

            const variableFieldName =
              variableFieldMapping[typeMetric.suffix]?.[variableKey];
            if (variableFieldName) {
              let actualVariableSuffix = variableFieldName;
              if (typeMetric.unit === "MW/MWh") {
                actualVariableSuffix = `${variableFieldName}_${selectedUnit.toLowerCase()}`;
              }

              const variableDataKey = `${prefix}_${actualVariableSuffix}`;
              groupedData[key][variableDataKey] =
                item[actualVariableSuffix as keyof ExchangeAnalysisDataItem];
            }
          });
        });

        if (item.mcv_mw !== undefined) {
          groupedData[key][`${prefix}_mcv_mw`] = item.mcv_mw;
        }
        if (item.mcv_mwh !== undefined) {
          groupedData[key][`${prefix}_mcv_mwh`] = item.mcv_mwh;
        }
      });

      const formattedChartData = Object.values(groupedData);

      const keys =
        activefunctionTab === "cumulative"
          ? ["Cumulative"]
          : Array.from(
              new Set(
                filteredData.map((item) => {
                  const exchangeName = item.exchange__name || "";
                  const productName = item.product__name || "";
                  return exchangeName && productName
                    ? `${exchangeName}_${productName}`
                    : "Cumulative";
                }),
              ),
            );

      return {
        chartData: formattedChartData,
        tableData: filteredData,
        chartKeys: keys,
      };
    },
    [
      activefunctionTab,
      selectedBlock,
      selectedExchanges,
      selectedProducts,
      selectedTypeMetrics,
      selectedVariableOptions,
      selectedUnit,
    ],
  );

  useEffect(() => {
    if (!rawData.length) {
      setChartData([]);
      setTableData([]);
      setChartKeys([]);
      setSummaryIntervalData([]);
      return;
    }

    const primaryProcessed = processDataForInterval(rawData, effectiveInterval);
    setChartData(primaryProcessed.chartData);
    setChartKeys(primaryProcessed.chartKeys);
    setTableData(primaryProcessed.tableData);

    const intervalSource = selectedIntervalRawData || rawData;
    const intervalProcessed = processDataForInterval(
      intervalSource,
      selectedInterval,
    );
    setSummaryIntervalData(intervalProcessed.chartData);
  }, [
    rawData,
    selectedIntervalRawData,
    effectiveInterval,
    selectedInterval,
    processDataForInterval,
  ]);

  const createChartObject = (
    key: string,
    index: number,
    suffix: string,
    displayName: string,
    stackSuffix: string,
    chartType: ChartType,
    unit: string,
    yAxisId: string,
  ) => {
    let actualUnit = unit;
    if (unit === "MW/MWh") {
      actualUnit = selectedUnit;
    }
    const dataKey = `${key}_${suffix}`;
    const legendName = `${normalizeDisplayLabel(key)} ${displayName}`;

    return {
      dataKey,
      name: legendName,
      type: chartType,
      unit: actualUnit,
      yAxisId: yAxisId,
      stackId:
        stackSuffix !== undefined && chartType === ChartType.Bar
          ? stackSuffix
          : undefined,
    };
  };

  function getLegends() {
    const legends: any[] = [];

    chartKeys.forEach((key, index) => {
      selectedTypeMetrics.forEach((typeMetric) => {
        let actualSuffix = typeMetric.suffix;
        if (typeMetric.unit === "MW/MWh") {
          actualSuffix = `${typeMetric.suffix}_${selectedUnit.toLowerCase()}`;
        }

        if (selectedVariableOptions.includes("actual")) {
          legends.push(
            createChartObject(
              key,
              index,
              actualSuffix,
              typeMetric.displayName,
              typeMetric.stackSuffix,
              typeMetric.chartType,
              typeMetric.unit,
              typeMetric.yAxisId,
            ),
          );
        }

        selectedVariableOptions.forEach((variableKey) => {
          if (variableKey === "actual") return;

          const variableOption = variableFilterOptions.find(
            (opt) => opt.key === variableKey,
          );
          const variableFieldName =
            variableFieldMapping[typeMetric.suffix]?.[variableKey];

          if (variableFieldName && variableOption) {
            let actualVariableSuffix = variableFieldName;
            if (typeMetric.unit === "MW/MWh") {
              actualVariableSuffix = `${variableFieldName}_${selectedUnit.toLowerCase()}`;
            }

            const variableLabelWithType = `${variableOption.label} (${typeMetric.displayName})`;

            legends.push(
              createChartObject(
                key,
                index,
                actualVariableSuffix,
                variableLabelWithType,
                variableFieldName,
                ChartType.Line,
                typeMetric.unit,
                typeMetric.yAxisId,
              ),
            );
          }
        });
      });
    });

    return legends;
  }

  const memoizedLegends = useMemo(
    () => getLegends(),
    [chartKeys, selectedTypeMetrics, selectedVariableOptions, selectedUnit],
  );

  const { visibleLegends, handleLegendSelect, selectedLegends } =
    useLegendFilter(memoizedLegends);

  const priceLegends = useMemo(
    () =>
      visibleLegends.map((legend: LegendKey) =>
        legend.unit === "Rs/KWh" ? legend : null,
      ),
    [visibleLegends],
  );

  const xDataKey = "x_axis";

  const viewHasSecondAxis =
    effectiveInterval.isTime ||
    effectiveInterval.values.includes("second_x_axis");
  const viewExtraKeys = viewHasSecondAxis
    ? ["x_axis", "second_x_axis"]
    : ["x_axis"];

  const viewExtraLabels = viewHasSecondAxis
    ? [
        effectiveInterval.isTime ? "Time Slot" : "Week",
        effectiveInterval.key === "slot" ? "Date" : "Month",
      ]
    : ["Date"];

  const summaryHasSecondAxis =
    selectedInterval.isTime ||
    selectedInterval.values.includes("second_x_axis");
  const summaryExtraKeys = summaryHasSecondAxis
    ? ["x_axis", "second_x_axis"]
    : ["x_axis"];

  const summaryExtraLabels = summaryHasSecondAxis
    ? [
        selectedInterval.isTime ? "Time Slot" : "Week",
        selectedInterval.key === "slot" ? "Date" : "Month",
      ]
    : ["Date"];

  const tableUnits: Record<string, string> = {};
  chartSuffixes.forEach((curr) => {
    let actualUnit = curr.unit;
    if (curr.unit === "MW/MWh") {
      actualUnit = selectedUnit;
    }
    tableUnits[curr.suffix] = actualUnit;

    Object.values(variableFieldMapping[curr.suffix] || {}).forEach(
      (fieldName) => {
        tableUnits[fieldName] = actualUnit;
      },
    );
  });

  const tablePar = {
    legends: visibleLegends,
    data: chartData,
    tableData: TableData,
    itemsPerPage: 96,
    extraKeys: viewExtraKeys,
    extraLabels: viewExtraLabels,
    filename: AddDatesWithFileName({
      fileName: "Exchange Analysis",
      dates: DefaultDates[activeTab]?.defaultRangeDate,
    }),
    customUnits: tableUnits,
    showTotal: false,
  };

  function secondDataKey() {
    if (effectiveInterval.values.includes("second_x_axis")) {
      return "second_x_axis";
    } else {
      return undefined;
    }
  }

  async function handleBarClick(item: any) {
    if (activefunctionTab === "cumulative") {
      return;
    }

    setLoading(true);
    try {
      const dataIndex = item.dataIndex;
      const clickedData = chartData[dataIndex];
      const seriesName = item.seriesName.split(" ");

      const exchange = seriesName[0];
      const product = seriesName[1];
      const slot = effectiveInterval.key === "slot" ? item.name : "no-slot";
      const date = clickedData.second_x_axis || clickedData.x_axis;

      const response = await fetchExchangeSlotData({
        exchange: exchange,
        product: product,
        slot: slot,
        date: date,
      });

      setModalHeader({
        exchange: exchange,
        product: product,
        slot: effectiveInterval.key === "slot" ? slot : "All Slots",
        date: date,
      });
      setModalData(response);
      setShowModal(true);
    } catch (error) {
      console.error("Error fetching slot data:", error);
    } finally {
      setLoading(false);
    }
  }

  const formatMetricValue = (value: number | null | undefined): string => {
    if (value === null || value === undefined) return "N/A";
    return typeof value === "number" ? value.toFixed(2) : "N/A";
  };

  return (
    <>
      <div className="gas-page-container">
        <NewFilterContainer>
          <div className="side-filters-container text-nowrap">
            <FilterMenu
              title="Interval"
              items={Intervals.map((item) => item.label)}
              selectedItems={[selectedInterval.label]}
              onSelect={(item, index) => {
                setSelectedInterval(Intervals[index]);
                setSelectedBlock([]);
              }}
            />

            <FilterMenu
              title="Unit"
              items={UNIT_OPTIONS}
              selectedItems={[selectedUnit]}
              onSelect={(item, index) => {
                setSelectedUnit(UNIT_OPTIONS[index]);
              }}
            />

            <FilterMenu
              title="Function"
              items={DataTabs.map(
                (tab) => tab.charAt(0).toUpperCase() + tab.slice(1),
              )}
              selectedItems={[
                activefunctionTab.charAt(0).toUpperCase() +
                  activefunctionTab.slice(1),
              ]}
              onSelect={(item, index) => {
                setActivefunctionTab(DataTabs[index]);
              }}
            />

            <FilterMenu
              title="Exchange"
              items={exchanges}
              selectedItems={selectedExchanges}
              setSelectedItems={(items: string[]) => {
                setSelectedExchanges(items);
              }}
            />

            <FilterMenu
              title="Product"
              items={products}
              selectedItems={selectedProducts}
              setSelectedItems={(items: string[]) => {
                setSelectedProducts(items);
              }}
            />

            <FilterMenu
              title="Type"
              items={typeFilterMetrics.map((item) => item.displayName)}
              selectedItems={selectedTypeMetrics.map(
                (item) => item.displayName,
              )}
              setSelectedItems={(selectedItems: string[]) => {
                if (!Array.isArray(selectedItems)) {
                  return;
                }

                const newSelectedMetrics = typeFilterMetrics.filter((item) => {
                  const isSelected = selectedItems.includes(item.displayName);
                  return isSelected;
                });
                setSelectedTypeMetrics(newSelectedMetrics);
              }}
            />
            <FilterMenu
              title="Cumulation"
              items={PRICE_TYPE_OPTIONS.map((item) => item.label)}
              selectedItems={[selectedPriceType.label]}
              onSelect={(item, index) => {
                setSelectedPriceType(PRICE_TYPE_OPTIONS[index]);
              }}
            />
            <FilterMenu
              title="Variable"
              items={variableFilterOptions.map((item) => item.label)}
              selectedItems={variableFilterOptions
                .filter((opt) => selectedVariableOptions.includes(opt.key))
                .map((opt) => opt.label)}
              setSelectedItems={(selectedItems: string[]) => {
                if (!Array.isArray(selectedItems)) {
                  return;
                }

                const newSelectedKeys = variableFilterOptions
                  .filter((opt) => selectedItems.includes(opt.label))
                  .map((opt) => opt.key);

                setSelectedVariableOptions(newSelectedKeys);
              }}
            />
          </div>
        </NewFilterContainer>

        <MainPageContainer>
          <SecondaryHeader ExchangeDataTabs={ExchangeDataTabs} />
          {/* Metrics Cards - Show in both Chart and Summary view */}
          {metrics &&
            (dataTab.label === "Chart" ||
              dataTab.label === "Summary" ||
              dataTab.label === "Table") && (
              <div className="flex flex-wrap gap-4 my-0 justify-center">
                <MetricChip
                  icon={<AvgIcon width={26} height={25} />}
                  items={[
                    {
                      label: "Wt. Avg Price",
                      value: metrics.weighted_avg_price.value,
                      unit: metrics.weighted_avg_price.unit,
                    },
                  ]}
                />
                <MetricChip
                  icon={<AvgIcon width={26} height={25} />}
                  items={[
                    {
                      label: "Avg Volume",
                      value: metrics.avg_volume.value,
                      unit: metrics.avg_volume.unit,
                    },
                  ]}
                />
                <MetricChip
                  icon={<TotalIcon width={26} height={25} />}
                  items={[
                    {
                      label: "Total Volume",
                      value: metrics.total_volume_mu.value,
                      unit: metrics.total_volume_mu.unit,
                    },
                  ]}
                />
              </div>
            )}
          <div className="gas-chart-container">
            {loading ? (
              <ChartAreaLoader
                title="Loading Collective"
                subtitle="Fetching exchange collective volume data…"
              />
            ) : (
              <>
                <div className="gas-heading gap-x-10">
                  <h2 className="chartHeading w-60">Collective</h2>

                  <div className="flex gap-x-6 justify-end">
                    <BlockRangeSelector
                      blocks={Array.from({ length: 96 }, (_, i) => i + 1)}
                      placeholder="Select Slot"
                      selectedOptions={selectedBlock.map((item) => item.value)}
                      setSelectedOptions={setSelectedBlock}
                    />
                  </div>
                </div>

                {dataTab.label === "Chart" && (
                  <ReMixChart
                    onBarClick={(item: any) => {
                      handleBarClick(item);
                    }}
                    data={chartData}
                    showBrush={chartData.length > 96 ? true : undefined}
                    brushStart={BrushStart.Start}
                    isTimeSlot={effectiveInterval.isTime}
                    onlyTitle={true}
                    legends={memoizedLegends}
                    onLegendSelect={handleLegendSelect}
                    defaultSelectedLegends={selectedLegends}
                    secondYAxisDomain={[0, 10]}
                    secondYAxisLabel="Rs/KWh"
                    yAxisLabel={selectedUnit}
                    xDataKey={xDataKey}
                    xTick={
                      effectiveInterval.key === "slot" &&
                      selectedBlock.length === 0
                        ? (value: any, index: any) => {
                            return renderHourTick(value, 10);
                          }
                        : effectiveInterval.key === "week"
                          ? renderQuarterTick
                          : undefined
                    }
                    secondXDataKey={secondDataKey()}
                  />
                )}
                {dataTab.label === "Summary" && (
                  <LegendSummarySlotTable
                    legends={visibleLegends}
                    avgData={chartData}
                    minMaxData={
                      summaryIntervalData.length > 0
                        ? summaryIntervalData
                        : chartData
                    }
                    avgTableData={TableData}
                    extraKeys={summaryExtraKeys}
                    extraLabels={summaryExtraLabels}
                    unit={selectedUnit}
                    priceLegends={priceLegends}
                    volumeUnitKey={selectedUnit.toLowerCase()}
                    block_key={
                      selectedInterval.key === "slot" ? "x_axis" : undefined
                    }
                    showWtdAvg={true}
                    priceType={selectedPriceType.key}
                    showTotal={false}
                  />
                )}
                {dataTab.label === "Table" && <ChartTable {...tablePar} />}
              </>
            )}
          </div>
          <div className="gna-footer-container">
            <div className="grid grid-cols-3 justify-between gap-5 ">
              <Tooltip
                note={
                  <div className="text-start">
                    <p>1. HPX | Hindustan Power Exchange</p>
                    <p>2. IEX | Indian Energy Exchange</p>
                    <p>3. PXIL | Power Exchange of India Limited</p>
                  </div>
                }
                text=" "
              >
                <p className="gna-footer-text">Source</p>
              </Tooltip>
              <p className="gna-footer-text col-span-1 text-center">
                Powered by <strong>GNA Energy</strong>
              </p>
              <strong className="gna-footer-text col-span-1 text-end">
                Beta Version
              </strong>
            </div>
          </div>
        </MainPageContainer>
      </div>
      <Modal
        height="calc(100vh - 100px)"
        title={`Top Buyers & Sellers (${modalHeader.exchange} - ${modalHeader.product} - ${modalHeader.slot} Time Block - ${modalHeader.date})`}
        description=""
        width="90%"
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setModalData({});
        }}
        children={
          <>
            <div
              style={{ height: "calc(100vh - 200px)" }}
              className="flex flex-col gap-4 h-full"
            >
              <PanelGroup className="flex-1 min-h-0" direction="horizontal">
                {/* Buyers Panel */}
                <Panel defaultSize={50} minSize={30} maxSize={70}>
                  <div className="bg-white rounded-xl border border-gray-200 h-full flex flex-col overflow-hidden shadow-sm">
                    <div
                      className="flex items-center justify-between px-5 py-3 border-b border-gray-100"
                      style={{
                        background:
                          "linear-gradient(90deg, #F59E0B14 0%, transparent 60%)",
                      }}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span
                          className="w-1 h-8 rounded-full shrink-0"
                          style={{ background: "#F59E0B" }}
                        />
                        <div className="min-w-0">
                          <h4 className="text-base flex gap-x-0 items-center font-semibold text-[#6F6D7C] leading-tight">
                            <span className="relative flex h-2 w-2">
                              <span
                                className="absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping"
                                style={{ background: "#F59E0B" }}
                              />
                              <span
                                className="relative inline-flex rounded-full h-2 w-2"
                                style={{ background: "#F59E0B" }}
                              />
                            </span>
                            &nbsp; Top Buyers
                          </h4>
                          <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                            <span className="text-gray-500">Total Volume:</span>
                            <span className="font-semibold text-[#6F6D7C]">
                              {(
                                modalData.buyer?.reduce(
                                  (s: number, i: { value: number }) =>
                                    s + (i.value || 0),
                                  0,
                                ) ?? 0
                              ).toLocaleString("en-IN", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{" "}
                              MWh
                            </span>
                            {(modalData.buyer?.length ?? 0) > 0 && (
                              <>
                                <span className="w-1 h-1 rounded-full bg-gray-300" />
                                <span className="flex items-center gap-1 truncate">
                                  <Crown
                                    className="w-3 h-3"
                                    style={{ color: "#F59E0B" }}
                                  />
                                  <span className="truncate text-[11px] text-gray-500">
                                    {
                                      [...(modalData.buyer || [])].sort(
                                        (a: any, b: any) =>
                                          (b.value || 0) - (a.value || 0),
                                      )[0]?.name
                                    }
                                  </span>
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <span
                        className="flex items-center gap-1.5 text-[11px] font-medium px-3 py-1 rounded-full shrink-0"
                        style={{ background: "#F59E0B1A", color: "#F59E0B" }}
                      >
                        <Zap
                          className="w-3.5 h-3.5"
                          fill="#F59E0B"
                          strokeWidth={2}
                        />
                        {modalData.buyer?.filter(
                          (b: { value: number }) => b.value > 0,
                        ).length || 0}{" "}
                        active
                      </span>
                    </div>
                    <div className="p-0 flex-1 overflow-x-hidden overflow-y-auto">
                      <div className="max-h-[calc(100vh-320px)] overflow-x-hidden overflow-y-auto">
                        <BuyerSellerChart
                          data={modalData.buyer || []}
                          showLegend={false}
                          type="buyer"
                          theme="light"
                        />
                      </div>
                    </div>
                  </div>
                </Panel>

                {/* Resize Handle */}
                <PanelResizeHandle className="w-[1px] mx-5 bg-gray-200 h-[80%] my-auto relative">
                  <div className="bg-white py-2 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                    <GripVertical color="#CDCDD3" />
                  </div>
                </PanelResizeHandle>

                {/* Sellers Panel */}
                <Panel defaultSize={50} minSize={30} maxSize={70}>
                  <div className="bg-white rounded-xl border border-gray-200 h-full flex flex-col overflow-hidden shadow-sm">
                    <div
                      className="flex items-center justify-between px-5 py-3 border-b border-gray-100"
                      style={{
                        background:
                          "linear-gradient(90deg, #34656D14 0%, transparent 60%)",
                      }}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span
                          className="w-1 h-8 rounded-full shrink-0"
                          style={{ background: "#34656D" }}
                        />
                        <div className="min-w-0">
                          <h4 className="text-base flex gap-x-0 items-center font-semibold text-[#6F6D7C] leading-tight">
                            <span className="relative flex h-2 w-2">
                              <span
                                className="absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping"
                                style={{ background: "#34656D" }}
                              />
                              <span
                                className="relative inline-flex rounded-full h-2 w-2"
                                style={{ background: "#34656D" }}
                              />
                            </span>
                            &nbsp; Top Sellers
                          </h4>
                          <div className="mt-0.5 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                            <span className="text-gray-500">Total Volume:</span>
                            <span className="font-semibold text-[#6F6D7C]">
                              {(
                                modalData.seller?.reduce(
                                  (s: number, i: { value: number }) =>
                                    s + (i.value || 0),
                                  0,
                                ) ?? 0
                              ).toLocaleString("en-IN", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{" "}
                              MWh
                            </span>
                            {(modalData.seller?.length ?? 0) > 0 && (
                              <>
                                <span className="w-1 h-1 rounded-full bg-gray-300" />
                                <span className="flex items-center gap-1 truncate">
                                  <Crown
                                    className="w-3 h-3"
                                    style={{ color: "#34656D" }}
                                  />
                                  <span className="truncate text-[11px] text-gray-500">
                                    {
                                      [...(modalData.seller || [])].sort(
                                        (a: any, b: any) =>
                                          (b.value || 0) - (a.value || 0),
                                      )[0]?.name
                                    }
                                  </span>
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      <span
                        className="flex items-center gap-1.5 text-[11px] font-medium px-3 py-1 rounded-full shrink-0"
                        style={{ background: "#34656D1A", color: "#34656D" }}
                      >
                        <Zap
                          className="w-3.5 h-3.5"
                          fill="#34656D"
                          strokeWidth={2}
                        />
                        {modalData.seller?.filter(
                          (s: { value: number }) => s.value > 0,
                        ).length || 0}{" "}
                        active
                      </span>
                    </div>
                    <div className="p-0 flex-1 overflow-x-hidden overflow-y-auto">
                      <div className="max-h-[calc(100vh-320px)] overflow-x-hidden overflow-y-auto">
                        <BuyerSellerChart
                          data={modalData.seller || []}
                          showLegend={false}
                          type="seller"
                          theme="light"
                        />
                      </div>
                    </div>
                  </div>
                </Panel>
              </PanelGroup>
            </div>
          </>
        }
      />
    </>
  );
};

export default ExchangeAnalysis;
